import { useEffect, useState } from "react";
import { api, formatApiError } from "../api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Check, X, Trash2, Pencil, Download } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", state: "", chapter: "", occupation: "", notes: "" };

export default function MembersAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/api/admin/members");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const visible = items.filter((m) => filter === "all" || m.status === filter);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.put(`/api/admin/members/${id}`, { status });
      setItems((arr) => arr.map((x) => (x.id === id ? data : x)));
      toast.success(`Marked ${status}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this member?")) return;
    try {
      await api.delete(`/api/admin/members/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      if (form.id) {
        const { id, created_at, status, ...payload } = form;
        const { data } = await api.put(`/api/admin/members/${form.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      } else {
        const { id, created_at, status, ...payload } = form;
        const { data } = await api.post("/api/admin/members", payload);
        setItems((arr) => [data, ...arr]);
      }
      setOpen(false);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const exportCsv = () => {
    const headers = ["Name", "Email", "Phone", "State", "Chapter", "Occupation", "Status", "Created"];
    const rows = items.map((m) => [m.name, m.email, m.phone, m.state, m.chapter, m.occupation, m.status, m.created_at]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `members-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6" data-testid="members-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-serif font-bold">Members</h1>
          <p className="text-sm text-muted-foreground mt-1">Approve, edit, or remove members.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv}><Download className="w-4 h-4 mr-2" />Export CSV</Button>
          <Button onClick={() => { setForm(empty); setOpen(true); }} className="bg-primary hover:bg-primary-dark text-white" data-testid="new-member-btn">
            <Plus className="w-4 h-4 mr-2" /> New member
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["all", "pending", "approved", "rejected"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`px-3 py-1 rounded-full text-xs capitalize ${filter === f ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
          >
            {f} {f !== "all" ? `(${items.filter((m) => m.status === f).length})` : `(${items.length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : visible.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">No members in this view.</Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Location</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((m) => (
                <tr key={m.id} className="border-t" data-testid={`member-row-${m.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{m.name}</div>
                    {m.occupation && <div className="text-xs text-muted-foreground">{m.occupation}</div>}
                  </td>
                  <td className="p-3">
                    <div>{m.email}</div>
                    {m.phone && <div className="text-xs text-muted-foreground">{m.phone}</div>}
                  </td>
                  <td className="p-3 text-xs">
                    {m.state} {m.chapter && `· ${m.chapter}`}
                  </td>
                  <td className="p-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${m.status === "approved" ? "bg-green-100 text-green-700" : m.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                      {m.status}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      {m.status === "pending" && (
                        <>
                          <Button size="sm" variant="outline" className="text-green-700" onClick={() => setStatus(m.id, "approved")} data-testid={`approve-${m.id}`}>
                            <Check className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-700" onClick={() => setStatus(m.id, "rejected")} data-testid={`reject-${m.id}`}>
                            <X className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" onClick={() => { setForm(m); setOpen(true); }} data-testid={`edit-member-${m.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-destructive" onClick={() => remove(m.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{form.id ? "Edit member" : "New member"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name-input" /></div>
              <div><Label>Email *</Label><Input value={form.email} disabled={!!form.id} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email-input" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              <div><Label>Chapter</Label><Input value={form.chapter} onChange={(e) => setForm({ ...form, chapter: e.target.value })} /></div>
              <div><Label>Occupation</Label><Input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary-dark text-white" data-testid="save-member-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
